import { useCallback, useState } from "react";

import { AppConstants, TodoConstants } from "constant";
import { TodoService } from "services";
import { StorageUtils } from "utils";
import { UserResponse } from "types";

import { DateTimeFields, validateDateTimeFields } from "../helpers";

const { MONTHS, TYPE_MAP, TIME_TYPE_MAP, COMPLETION_TYPE_MAP } = TodoConstants;
const { STORAGE_USER } = AppConstants;

// Helper to get current user ID for initial assignee
const getCurrentUserId = (): string[] => {
  const user = StorageUtils.getObject(STORAGE_USER) as UserResponse | undefined;
  return user?.id ? [String(user.id)] : [];
};

export interface FormData {
  title: string;
  description: string;
  assignees: string[];
  client: string | null;
  assigneeType: string;
  timeType: string;
  todoType: string | null;
  urgent: boolean;
  important: boolean;
  recurring: boolean;
  recurringType: string | null;
}

const initialFormData: FormData = {
  title: "",
  description: "",
  assignees: [],
  client: null,
  assigneeType: "everyone",
  timeType: "deadline",
  todoType: null,
  urgent: false,
  important: false,
  recurring: false,
  recurringType: null,
};

const initialDateTime: DateTimeFields = {
  day: null,
  month: null,
  year: null,
  time: "",
};

interface UseTodoFormProps {
  onClose: () => void;
}

export function useTodoForm({ onClose }: UseTodoFormProps) {
  // Initialize with current user as assignee
  const [formData, setFormData] = useState<FormData>(() => ({
    ...initialFormData,
    assignees: getCurrentUserId(),
  }));
  const [dateTime, setDateTime] = useState<DateTimeFields>(initialDateTime);
  const [endDateTime, setEndDateTime] = useState<DateTimeFields>(initialDateTime);
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  const resetForm = useCallback(() => {
    setFormData({
      ...initialFormData,
      assignees: getCurrentUserId(),
    });
    setDateTime(initialDateTime);
    setEndDateTime(initialDateTime);
    setErrors({});
  }, []);

  const clearError = useCallback((field: string) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: false } : prev));
  }, []);

  const updateFormData = useCallback((updates: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  }, []);

  const updateDateTime = useCallback((updates: Partial<DateTimeFields>) => {
    setDateTime((prev) => ({ ...prev, ...updates }));
  }, []);

  const updateEndDateTime = useCallback((updates: Partial<DateTimeFields>) => {
    setEndDateTime((prev) => ({ ...prev, ...updates }));
  }, []);

  const validateIntervalErrors = useCallback(
    (startDT: DateTimeFields = dateTime, endDT: DateTimeFields = endDateTime) => {
      const errors = validateDateTimeFields(startDT, endDT, true);
      setErrors((prev) => ({
        ...prev,
        endDay: errors.endDay ?? false,
        endMonth: errors.endMonth ?? false,
        endYear: errors.endYear ?? false,
        endTime: errors.endTime ?? false,
      }));
    },
    [dateTime, endDateTime],
  );

  const validateForm = useCallback(() => {
    const newErrors: Record<string, boolean> = {};

    // Validate form fields
    if (!formData.title.trim()) newErrors.title = true;
    if (formData.assignees.length === 0) newErrors.assignees = true;
    if (!formData.todoType) newErrors.todoType = true;

    // Validate date/time using shared helper
    const isInterval = formData.timeType === "interval";
    const dateErrors = validateDateTimeFields(
      dateTime,
      isInterval ? endDateTime : undefined,
      isInterval,
    );

    // Merge all errors
    Object.assign(newErrors, dateErrors);

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, dateTime, endDateTime]);

  const buildDateTime = useCallback((dt: DateTimeFields) => {
    if (!dt.day || !dt.month || !dt.year || !dt.time) return undefined;
    const monthIndex = MONTHS.indexOf(dt.month);
    const [hours, minutes] = dt.time.split(":").map(Number);

    // Format as ISO string without UTC conversion
    const year = Number(dt.year);
    const month = String(monthIndex + 1).padStart(2, "0");
    const day = String(Number(dt.day)).padStart(2, "0");
    const hour = String(hours || 0).padStart(2, "0");
    const min = String(minutes || 0).padStart(2, "0");

    return `${year}-${month}-${day}T${hour}:${min}:00`;
  }, []);

  const handleSubmit = useCallback(() => {
    if (!validateForm()) return;

    const requestData: TodoService.TodoCreateRequest = {
      title: formData.title || "Novi zadatak",
      description: formData.description || undefined,
      type: formData.todoType ? TYPE_MAP[formData.todoType] : "INTERNAL_TASK",
      timeType: TIME_TYPE_MAP[formData.timeType],
      completionType: COMPLETION_TYPE_MAP[formData.assigneeType],
      urgent: formData.urgent,
      important: formData.important,
      archive: false,
      recurring: formData.recurring,
      recurringType: formData.recurring && formData.recurringType
        ? (formData.recurringType as 'DAILY' | 'WEEKLY' | 'MONTHLY')
        : undefined,
    };

    // Set date/time based on timeType
    if (formData.timeType === "deadline") {
      requestData.dueDate = buildDateTime(dateTime);
    } else if (formData.timeType === "exact") {
      requestData.startTime = buildDateTime(dateTime);
    } else if (formData.timeType === "interval") {
      requestData.startTime = buildDateTime(dateTime);
      requestData.endTime = buildDateTime(endDateTime);
    }

    // Add clientId only if selected
    if (formData.client) {
      requestData.clientId = Number(formData.client);
    }

    // Add users (employees from "Za" field)
    if (formData.assignees.length > 0) {
      requestData.users = formData.assignees.map((id) => ({ userId: Number(id) }));
    }

    TodoService.todoCreate(requestData);
    resetForm();
    onClose();
  }, [formData, dateTime, endDateTime, validateForm, buildDateTime, resetForm, onClose]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  return {
    // State
    formData,
    dateTime,
    endDateTime,
    errors,
    // Updaters
    updateFormData,
    updateDateTime,
    updateEndDateTime,
    clearError,
    resetForm,
    // Validation
    validateIntervalErrors,
    // Actions
    handleSubmit,
    handleClose,
  };
}
